import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import cn from "clsx";
import moment from "moment";
import "@/components/modal/modalMobileVerification/style.scss";
import { ButtonWithLoader } from "@/components/common/buttons";
import {
  TurnstileCaptcha,
  TurnstileCaptchaRef
} from "@/components/captcha/captcha";
import { useAppSelector } from "@/redux/store";
import Loader from "@/components/loader";
import { commonLoaderStyle, LOADER_TYPES } from "@/constants";
import { selectSettings } from "@/util/selectors";

type Props = {
  disableBtn?: boolean;
  phone: string;
  otp: string;
  handleOTPChange: (val: string) => void;
  verifyOtp: () => void;
  otpValid: boolean;
  sendOtp?: (e?: any) => void | Promise<void>;
  sendOtpCount?: number;
  errorMessage?: string;
  showLoader?: boolean;
  expiresAtMs?: number | null;
  showMobileVerification?: boolean;
  onSwitchToMobile?: any;
  resending?: boolean;
  showCaptcha?: boolean;
};

const loaderStyle = {
  ...commonLoaderStyle,
  backgroundColor: "inherit"
};

const SendOTP: React.FC<Props> = ({
  disableBtn,
  phone,
  otp,
  handleOTPChange,
  verifyOtp,
  otpValid,
  sendOtp,
  sendOtpCount = 0,
  errorMessage = "",
  showLoader = false,
  expiresAtMs = null,
  showMobileVerification = false,
  onSwitchToMobile,
  resending = false,
  showCaptcha
}) => {
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [isInteractiveCaptcha, setIsInteractiveCaptcha] = useState(false);
  const turnstileCaptchaToken = useAppSelector(
    state => state.authReducer.turnstileCaptchaToken
  );
  const settings = useAppSelector(selectSettings);
  const valueCaptchaFromCMS = settings?.captcha?.sendOrResendSmsOtp;
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const isAutomaticCaptcha =
    !captchaLoading &&
    !isInteractiveCaptcha &&
    valueCaptchaFromCMS &&
    !turnstileCaptchaToken;
  const shouldShowLoader = isAutomaticCaptcha || captchaLoading;
  const { t } = useTranslation("auth");

  const calcInit = () => {
    if (!expiresAtMs) return 0;
    const diff = Math.floor((expiresAtMs - Date.now()) / 1000);
    return diff > 0 ? diff : 0;
  };

  const [secondsLeft, setSecondsLeft] = useState<number>(calcInit());
  const [localError, setLocalError] = useState<string>(errorMessage || "");

  useEffect(() => {
    setSecondsLeft(calcInit());
  }, [expiresAtMs]);

  useEffect(() => {
    if (!showLoader && !resending) {
      setLocalError(errorMessage || "");
    }
  }, [errorMessage, showLoader, resending]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }
    const id = setInterval(() => {
      setSecondsLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const disabledResend = secondsLeft > 0 || resending;

  const timeStr = useMemo(
    () =>
      moment().startOf("day").seconds(Math.max(0, secondsLeft)).format("mm:ss"),
    [secondsLeft]
  );

  const onResendClick = async (e: any) => {
    if (!sendOtp || disabledResend) return;
    await sendOtp(e);
  };

  const onOtpChange = (val: string) => {
    if (localError) setLocalError("");
    handleOTPChange(val);
  };

  const canVerify = otp.length === 4 && !localError && !showLoader;

  return (
    <div className="otp_verify generic_modal_content d-flex flex-column login_otp_content">
      <h2 className="title">{t("mobileVerification")}</h2>
      <p className="grey_head">
        {t("enter4DigitOtp")} <span>+{phone}</span>
      </p>

      <div
        className={cn("form-group mobile_otp", {
          "flex-wrap": Boolean(localError) && !otpValid
        })}
      >
        <OtpInput
          onChange={onOtpChange}
          shouldAutoFocus
          numInputs={4}
          value={otp}
          separator={<span />}
        />
      </div>

      {sendOtp && (
        <>
          <div
            className={cn("resend_otp_btn mt-3", { disabled: disabledResend })}
          >
            {disabledResend ? (
              <>
                {resending ? (
                  <>
                    <span className="resend-otp">
                      {t("resendOtp").toUpperCase()}
                    </span>
                  </>
                ) : (
                  <>
                    {t("resendOtpIn")}
                    <span className="resend_otp_time">{timeStr}</span>
                  </>
                )}
              </>
            ) : (
              <div className="captcha_otp_container">
                {valueCaptchaFromCMS && showCaptcha && (
                  <TurnstileCaptcha
                    ref={captchaRef}
                    className="mt-4 mb-4"
                    beforeLoading={() => setCaptchaLoading(true)}
                    afterLoading={() => {
                      setCaptchaLoading(false);
                    }}
                    onInterectiveChallangeStarted={() => {
                      setIsInteractiveCaptcha(true);
                    }}
                    onToken={() => setCaptchaLoading(false)}
                    onTokenExpired={() => setCaptchaLoading(false)}
                  />
                )}
                <div className="resent-otp-container position-relative">
                  {valueCaptchaFromCMS && showCaptcha ? (
                    !disabledResend && !shouldShowLoader ? (
                      <span onClick={onResendClick} className="resend-otp">
                        {t("resendOtp").toUpperCase()}
                      </span>
                    ) : (
                      <Loader
                        type={LOADER_TYPES.DOTS}
                        visible
                        style={loaderStyle}
                        size={10}
                      />
                    )
                  ) : (
                    <span onClick={onResendClick} className="resend-otp">
                      {t("resendOtp").toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {showMobileVerification && !disabledResend && (
            <div className="otp_switch_to_mobile">
              {t("otpOnMobileCta")}
              <button
                type="button"
                className="link link_accent"
                onClick={onSwitchToMobile}
              >
                {t("clickHere")}
              </button>
            </div>
          )}
        </>
      )}

      {localError && !showLoader && !resending && (
        <span
          className={cn("error", {
            "position-relative w-100 text-center ": !otpValid
          })}
        >
          {localError}
        </span>
      )}

      <ButtonWithLoader
        className={cn(
          "round_btn form_black_btn small_btn position-relative login_otp_btn",
          { disable_order_btn: !canVerify }
        )}
        onClick={verifyOtp}
        disabled={!canVerify || disableBtn}
        showLoader={showLoader}
      >
        {t("verify")}
      </ButtonWithLoader>
    </div>
  );
};

export default SendOTP;
